/* product-rail.js — paging, end state and keyboard for every product rail.
 *
 * Luke, 2026-09-21: "recently viewed and other components like this should be
 * carousels and also should have the same features like quick view, left/right
 * for cycling through that product's images, add to project and so on."
 *
 * The rail's LAYOUT is entirely CSS (snippets/product-rail.liquid + the .prail
 * block in theme.css): a flex track with scroll-snap, cells whose width is the
 * .product-grid cell width at that breakpoint, native swipe on touch. This
 * file adds only the three things CSS cannot answer:
 *
 *   1. does the track actually overflow?  -> the nav blocks ship `hidden` and
 *      are revealed only once measured, so a rail of four in a four-up row has
 *      no controls at all rather than two dead ones.
 *   2. where is the scroller?             -> [disabled] at each end, plus
 *      .at-start/.at-end for the phone fade.
 *   3. keyboard                           -> Left/Right page the rail when the
 *      focus is inside the track.
 *
 * NOT a carousel library and deliberately not one: no autoplay, no cloned
 * slides, no transform track, no dependency. The scroller IS the browser's,
 * so momentum, snap, rubber-banding, RTL and a trackpad's two-finger swipe all
 * behave the way the platform does and cost nothing.
 *
 * WHY THE NAV DOES NOT MOVE THE FOCUS. Paging scrolls; it does not focus the
 * card that arrives. Moving focus would fight the delegated card handlers
 * (ui.js reveals .card__arrow on :hover and :focus-visible) and would make a
 * keyboard user's Tab order jump around the page. Tab still walks the cards in
 * DOM order and the browser scrolls each one into view natively.
 *
 * Classic script, deferred, loaded after ui.js in layout/theme.liquid.
 * window.STRail.sync() is the hook for any renderer that fills a track after
 * load — assets/ui.js calls it once the recently-viewed rail is painted.
 */
(function () {
  'use strict';
  if (window.STRail) return;

  var EPS = 2;                       // sub-pixel slack on scroll maths
  var mqReduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)');

  var trackOf = function (rail) { return rail.querySelector('[data-rail-track]'); };

  /* One cell + one gap: the step a press moves by. Read from the DOM rather
   * than recomputed from --rail-cols, because the cell width is a percentage
   * of a box only the layout knows and the breakpoint rules override it. */
  var stepOf = function (track) {
    var cell = track.querySelector('.prail__cell');
    if (!cell) return track.clientWidth;
    var gap = parseFloat(getComputedStyle(track).columnGap);
    if (!isFinite(gap)) gap = 0;
    var w = cell.getBoundingClientRect().width;
    return w > 0 ? w + gap : track.clientWidth;
  };

  function sync(rail) {
    var track = trackOf(rail);
    if (!track) return;
    var max = track.scrollWidth - track.clientWidth;
    var over = max > EPS;
    var x = track.scrollLeft;
    rail.classList.toggle('is-scrollable', over);
    rail.classList.toggle('at-start', !over || x <= EPS);
    rail.classList.toggle('at-end', !over || x >= max - EPS);
    var prev = rail.querySelector('[data-rail-prev]');
    var next = rail.querySelector('[data-rail-next]');
    if (prev) { prev.hidden = !over; prev.disabled = !over || x <= EPS; }
    if (next) { next.hidden = !over; next.disabled = !over || x >= max - EPS; }
  }

  function page(rail, dir) {
    var track = trackOf(rail);
    if (!track) return;
    var step = stepOf(track);
    // a whole screenful of cells, never a fraction of one — landing mid-card
    // is what makes a hand-rolled rail feel broken
    var n = Math.max(1, Math.floor((track.clientWidth + EPS) / step));
    var behavior = (mqReduce && mqReduce.matches) ? 'auto' : 'smooth';
    try { track.scrollBy({ left: dir * step * n, behavior: behavior }); }
    catch (e) { track.scrollLeft += dir * step * n; }
  }

  function syncAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var rails = scope.querySelectorAll('[data-rail]');
    for (var i = 0; i < rails.length; i++) sync(rails[i]);
  }

  /* Per-rail wiring, once. A rail can arrive after load three ways — the PDP's
   * recommendations land through the Section Rendering API (ui.js), the
   * recently-viewed track is filled from localStorage, and the theme editor
   * re-renders a section — so observe rather than only sweeping at boot. */
  var wired = new WeakSet();
  function wire(rail) {
    if (wired.has(rail)) return;
    wired.add(rail);
    var track = trackOf(rail);
    if (!track) return;
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; sync(rail); });
    }, { passive: true });
    if (window.ResizeObserver) {
      // the TRACK for a viewport change, the first CELL because .card carries
      // content-visibility:auto and settles its height after first paint
      var ro = new ResizeObserver(function () { sync(rail); });
      ro.observe(track);
      var cell = track.querySelector('.prail__cell');
      if (cell) ro.observe(cell);
    } else {
      addEventListener('resize', function () { sync(rail); });
    }
    sync(rail);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-rail-prev],[data-rail-next]');
    if (!btn) return;
    var rail = btn.closest('[data-rail]');
    if (!rail) return;
    e.preventDefault();
    page(rail, btn.hasAttribute('data-rail-next') ? 1 : -1);
  });

  /* Left/Right page the rail when the focus is inside the track — EXCEPT on a
   * card's own image pager, where those keys belong to the photo, and except
   * in a field, where they belong to the caret. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('[data-rail-track]')) return;
    if (t.closest('.card__arrow')) return;
    var tag = (t.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable) return;
    var rail = t.closest('[data-rail]');
    if (!rail || !rail.classList.contains('is-scrollable')) return;
    e.preventDefault();
    page(rail, e.key === 'ArrowRight' ? 1 : -1);
  });

  var boot = function () { document.querySelectorAll('[data-rail]').forEach(wire); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // images settle after the cards are laid out; one late sweep catches a track
  // that only overflows once its photos have real dimensions
  addEventListener('load', function () { boot(); syncAll(); });

  if (window.MutationObserver) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('[data-rail]')) wire(n);
          if (n.querySelectorAll) n.querySelectorAll('[data-rail]').forEach(wire);
          // cells appended into a track that is already wired
          var rail = n.closest && n.closest('[data-rail]');
          if (rail) sync(rail);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  window.STRail = { sync: syncAll, wire: wire, page: page };
})();
